/**
 * File System Cache Manager
 * Manages File System API cache (Chrome/Edge only)
 */

import fileSystemStorage from './fileSystemStorage';

class CacheManager {
  /**
   * Clear all File System cache
   */
  async clearAll() {
    try {
      if (fileSystemStorage.isFileSystemAccessSupported()) {
        const hasAccess = await fileSystemStorage.restoreDirectoryHandle();
        if (hasAccess) {
          await fileSystemStorage.clearAllCache();
        }
      }
    } catch (error) {
      console.error('[CacheManager] Error clearing cache:', error);
      throw error;
    }
  }

  /**
   * Clear cache for specific study
   * @param {string} StudyInstanceUID
   */
  async clearStudy(StudyInstanceUID) {
    try {
      if (fileSystemStorage.isFileSystemAccessSupported()) {
        await fileSystemStorage.removeStudy(StudyInstanceUID);
      }
    } catch (error) {
      console.error('[CacheManager] Error clearing study cache:', error);
      throw error;
    }
  }

  /**
   * Clear images for specific study
   * @param {string} StudyInstanceUID
   */
  async clearStudyImages(StudyInstanceUID) {
    try {
      if (fileSystemStorage.isFileSystemAccessSupported()) {
        await fileSystemStorage.removeStudy(StudyInstanceUID);
      }
    } catch (error) {
      console.error('[CacheManager] Error clearing study images:', error);
      throw error;
    }
  }

  /**
   * Get File System cache statistics
   * @returns {Promise<Object>}
   */
  async getStats() {
    try {
      if (!fileSystemStorage.isFileSystemAccessSupported()) {
        return {
          supported: false,
          message: 'File System API not supported',
        };
      }

      const cachedStudies = await fileSystemStorage.getCachedStudies();

      return {
        supported: true,
        cachedStudyCount: cachedStudies ? cachedStudies.length : 0,
        cachedStudies: cachedStudies || [],
        storageType: 'File System API (unlimited)',
      };
    } catch (error) {
      console.error('[CacheManager] Error getting stats:', error);
      return null;
    }
  }

  /**
   * Get all cached studies
   * For UI cache management display
   * @returns {Promise<Array>}
   */
  async getAllCachedStudies() {
    try {
      if (!fileSystemStorage.isFileSystemAccessSupported()) {
        return [];
      }

      const studies = await fileSystemStorage.getCachedStudies();
      return studies || [];
    } catch (error) {
      console.error('[CacheManager] Error getting all cached studies:', error);
      return [];
    }
  }

  /**
   * Export cache info for debugging
   * @returns {Promise<Object>}
   */
  async export() {
    try {
      const stats = await this.getStats();
      return {
        timestamp: Date.now(),
        stats,
      };
    } catch (error) {
      console.error('[CacheManager] Error exporting cache:', error);
      return null;
    }
  }
}

// Singleton instance
const cacheManager = new CacheManager();

export default cacheManager;
